import moment from 'moment'
import React, { ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../../helpers/connectRedux'
import { Link } from 'react-router-dom'
import { RouteComponentProps, withRouter } from '../../../helpers/withRouter'

import { Button, Modal, Page, ReservationDetailsCard, Spinner } from '../../../components'
import { AuthenticatedRoutable, IActivePass, IClientInfo, ICoordinate, IPaymentState, IPaymentStatus, IReservation, ITicketingType } from '../../../models'
import { urls } from '../../../routing'
import { LiteralsService, PassesService, ReservationService, ServicesInfoService } from '../../../services'
import {
  clearReservationDetailsAction,
  fetchActivePassesAction,
  confirmNewReservationFailedAction,
  deleteReservationAction,
  fetchClientInfoAction,
  fetchReservationDetailsAction,
  setDoingPayment,
  setErrorCancelReservationModalAction,
  setExpeditionStatusAction,
  resetNewReservationFieldsAction,
  setNewReservationFieldsAction,
  setNotificationAction,
  setPaymentFailed,
  setPaymentSuccess,
  setQrCodeAndDetails,
  setSelectedReservationModalAction
} from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import { StyledArticle, StyledModalDiv } from './styles'
import { images } from '../../../images'
import { IExpeditionStatus } from '../../../models/expedition-status'
import { ConfirmPaymentModal } from '../../../containers/NewReservation/ConfirmPaymentModal'
import { SelectActivePassModal } from '../../../containers/NewReservation/SelectActivePassModal'
import {
  appsCancelBookingReasonsSelected,
  appsMandatoryCancellationReasonSelected,
  appsTemporalPassesSelected
} from '../../../helpers/app'
import { Select } from '../../../components'
import { ISetNotificationAction } from '../../../store/types'
import { ElementsConsumer } from '@stripe/react-stripe-js'
import theme from '../../../theme'
import { AnyAction } from 'redux'

// Flip to false to hide the edit button.
const SHOW_EDIT_BUTTON = true

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  reservation: IReservation
  isDeletingReservation: boolean
  paymentState: IPaymentState
  paymentMethodId: string
  termsLink: string
  showErrorCancelReservationModal: boolean
  clientInfo: IClientInfo | null
  activePasses: IActivePass[]
}

interface IState {
  busLocation: ICoordinate | null
  expeditionStatus: string
  delay: number
  isDeleteModalVisible: boolean
  isConfirmPaymentModalVisible: boolean
  displaySelectPassModal: boolean
  isUsingPass: boolean
  // reason the traveller gives for cancelling; travels to the backend as the comment
  cancellationReason: string
  // whether the reservation's service books round trips; unknown until its service is loaded
  isRoundTripService?: boolean
}

class ReservationDetails extends AuthenticatedRoutable<IProps, IState> {
  private busInterval: any
  private _stripe: any

  private get shouldShowInfo(): boolean {
    const { expeditionStatus } = this.state
    const { reservation } = this.props
    return !reservation?.cancelled && expeditionStatus.toLowerCase() === 'ongoing'
  }

  private get infoText(): string {
    const { delay, busLocation } = this.state
    const { reservation } = this.props

    if(busLocation === null){
      return LiteralsService.get("nonAvailableLocation", true);
    }

    const now = moment()
    const beginningStopWithDelay = moment(
      reservation?.beginningStop.dateTime
    ).add(delay, 'minutes')

    const endStopWithDelay = moment(
      reservation?.seats[0].exitStop.dateTime
    ).add(delay, 'minutes')

    if (now.isSameOrBefore(beginningStopWithDelay)) {
      let minutesToArrive = beginningStopWithDelay.diff(moment(), 'minutes')
      if (minutesToArrive < 1) {
        return `${LiteralsService.get('pickUpInLessThan', true)} 1 min`
      }
      return `${LiteralsService.get('pickUpSoon', true)} ${minutesToArrive} min`
    } else if (now.isBetween(beginningStopWithDelay, endStopWithDelay)) {
      return LiteralsService.get('onGoing', true)
    } else return LiteralsService.get('past', true)
  }

  private get info(): ReactNode {
    return (
      <div className="info-bubble">
        <img src={images.information} alt='info' />
        <span className="info-text">{this.infoText}</span>
      </div>
    )
  }

  constructor(props: IProps) {
    super(props)

    this.state = {
      busLocation: null,
      expeditionStatus: '',
      delay: 0,
      isDeleteModalVisible: false,
      isConfirmPaymentModalVisible: false,
      displaySelectPassModal: false,
      isUsingPass: false,
      cancellationReason: ''
    }
  }

  public componentDidMount(): void {
    const { dispatch, location } = this.props
    let reservationId, isCancelled, relatedReservationId = undefined;
    if (location.state) {
      const { id, cancelled, relatedId } = location.state
      reservationId = id
      isCancelled = cancelled
      relatedReservationId = relatedId
    } else {
      const splitUrl = location.pathname.split('/')
      reservationId = splitUrl[splitUrl.length - 1]
    }

    super.componentDidMount()

    // Leaving an edit halfway (e.g. with the browser back button) must not leave it behind for
    // the next new booking.
    dispatch(resetNewReservationFieldsAction())
    if (!this.props.clientInfo) dispatch(fetchClientInfoAction())

    if (this.props.reservation) this.loadIsRoundTripService()
    this.busIntervalFunction()
    dispatch(fetchReservationDetailsAction(reservationId, isCancelled, relatedReservationId))
    if (appsTemporalPassesSelected?.enabled) dispatch(fetchActivePassesAction())
  }

  // A pass covers the trip only if it is one of those the reservation accepts, and
  // it still has a usage left for every seat.
  private get matchingActivePasses(): IActivePass[] {
    const { activePasses, reservation } = this.props
    const allowedIds = reservation?.temporalPassIds

    if (!activePasses?.length || !allowedIds?.length) return []

    return activePasses.filter(pass => allowedIds.includes(pass.temporalPass.id))
  }

  // Amount still owed = full price - what was already paid (rounded to cents). paymentPrice alone is the full
  // ticket price, so an edited booking that was paid before would otherwise be asked for the whole amount again.
  private get priceLeftToPay(): number {
    const { reservation } = this.props
    return Math.round((reservation.paymentPrice - (reservation.amountPaid ?? 0)) * 100) / 100
  }

  private get canUsePass(): boolean {
    return !!appsTemporalPassesSelected?.enabled && this.matchingActivePasses.length > 0
  }

  private toggleSelectPassModal = () => {
    if (this.state.isUsingPass) return
    this.setState({ displaySelectPassModal: !this.state.displaySelectPassModal })
  }

  private useActivePass = async (activePassId: number) => {
    const { dispatch, reservation, history } = this.props

    this.setState({ isUsingPass: true })
    try {
      await PassesService.usePass(activePassId, reservation.id)
      dispatch(setNotificationAction({ isError: false, label: LiteralsService.get('bookPay', true) }))
      this.setState({ isUsingPass: false, displaySelectPassModal: false })
      dispatch(fetchActivePassesAction())
      history.push(urls.reservations)
    } catch (error) {
      dispatch(setNotificationAction({ isError: true, label: LiteralsService.get('payFailed', true) }))
      this.setState({ isUsingPass: false })
    }
  }

  componentDidUpdate(prevProps: IProps): void {
    const { expeditionStatus } = this.state

    if (this.props.reservation?.id !== prevProps.reservation?.id) this.loadIsRoundTripService()

    if (
      expeditionStatus !== IExpeditionStatus.Closed &&
      expeditionStatus !== IExpeditionStatus.Cancelled &&
      expeditionStatus !== IExpeditionStatus.Deleted
    ) {
      if (!this.busInterval) this.busInterval = setInterval(() => this.busIntervalFunction(), 3500)
    } else {
      clearInterval(this.busInterval)
      this.busInterval = null;
    }
  }

  // The reservation carries a basic service info, without its round trip setting.
  private loadIsRoundTripService = async () => {
    const { reservation } = this.props
    this.setState({ isRoundTripService: undefined })
    if (!reservation?.service?.id) return

    try {
      // The service comes with its one service info.
      const service: any = await ServicesInfoService.getServicesByServiceId(reservation.service.id)
      const serviceInfo = service?.serviceInfo
      if (this.props.reservation?.id === reservation.id) this.setState({ isRoundTripService: !!serviceInfo?.roundTrips })
    } catch (error) {
      // Without knowing, editing stays hidden.
    }
  }

  /**
   * A round trip service only updates both trips at once (the backend refuses to update one of them
   * alone), so a trip whose pair is not known cannot be edited.
   */
  private get canEdit(): boolean {
    const { reservation } = this.props
    const { isRoundTripService } = this.state
    if (!SHOW_EDIT_BUTTON || isRoundTripService === undefined) return false
    return !isRoundTripService || !!reservation?.relatedReservationId
  }

  public componentWillUnmount(): void {
    clearInterval(this.busInterval)
    this.busInterval = null;
    this.props.dispatch(clearReservationDetailsAction())
  }

  public render(): ReactNode {
    const { reservation, history, isDeletingReservation, paymentState, termsLink, showErrorCancelReservationModal, clientInfo } = this.props
    const { delay, busLocation, expeditionStatus, isDeleteModalVisible, isConfirmPaymentModalVisible } = this.state

    if (!reservation) {
      return (
        <Page className="authenticated">
          <StyledArticle style={{ justifyContent: 'center' }}>
            <Spinner />
          </StyledArticle>
        </Page>
      )
    }

    // Only with something left to pay: a refund due after an edit is settled from the edit flow, not here.
    const hasPendingPayment = reservation.expeditionStatus !== IExpeditionStatus.Closed && 
      this.priceLeftToPay > 0 &&
      (
        reservation.ticketingType === ITicketingType.Hybrid && reservation.paymentStatus !== IPaymentStatus.SUCCEEDED ||
        reservation.ticketingType === ITicketingType.Online && reservation.paymentStatus === IPaymentStatus.FAILED
      )

    return (
      <>
        <Page className="authenticated">
            <ElementsConsumer>
              {({ stripe }) => {
                this._stripe = stripe
                return (
                  <StyledArticle>
                    <section className="actions">
                      <img src={ images['goBack'] } className='backButton' onClick={ () => history.push(urls.reservations) } />
                    </section>
                    <section className="details-container">
                      <ReservationDetailsCard
                        reservation={ reservation }
                        onFavoriteClick={ this.displayFavoriteModal }
                        busLocation={ busLocation }
                        aprox={ expeditionStatus.toLowerCase() === 'open' }
                        extraInfoMessage={ expeditionStatus.toLowerCase() === 'open' }
                        onQRClick={ this.handleQRClick }
                        onExpandMapClick={ this.openFullscreenMap }
                      />
                      { this.shouldShowInfo && this.info }
                      <footer>
                        {
                          reservation.expeditionStatus !== IExpeditionStatus.Closed &&
                          reservation.expeditionStatus !== IExpeditionStatus.Cancelled &&
                          reservation.expeditionStatus !== IExpeditionStatus.Deleted &&
                          <>
                            <Button
                              title={ LiteralsService.get('cancel') }
                              onPress={ this.toggleDeleteModalVisibility }
                              styles={{
                                backgroundColor: theme.colors.white,
                                borderColor: theme.colors.primary,
                                borderWidth: 2,
                                boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
                              }}
                              upper
                            />
                            {
                              this.canEdit &&
                              <Button
                                title={ LiteralsService.get('edit') }
                                onPress={ this.editReservation }
                                styles={{
                                  backgroundColor: !hasPendingPayment ? theme.colors.primary : theme.colors.white,
                                  borderColor: theme.colors.primary,
                                  borderWidth: 2,
                                  boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
                                }}
                                filled={!hasPendingPayment}
                                upper
                              />
                            }
                            {
                              hasPendingPayment &&
                              <Button
                                title={ `${LiteralsService.get('pay')} ${this.priceLeftToPay}€` }
                                onPress={ this.toggleConfirmPaymentModal }
                                styles={{
                                  borderWidth: 2,
                                  boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
                                }}
                                filled
                                upper
                              />
                            }
                            {
                              hasPendingPayment && this.canUsePass &&
                              <Button
                                title={ LiteralsService.get('usePass') }
                                onPress={ this.toggleSelectPassModal }
                                styles={{
                                  backgroundColor: theme.colors.white,
                                  borderColor: theme.colors.primary,
                                  borderWidth: 2,
                                  boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
                                }}
                                upper
                              />
                            }
                          </>
                        }
                      </footer>
                    </section>
                  </StyledArticle>
                )
              }}
            </ElementsConsumer>
        </Page>
        {
          this.state.displaySelectPassModal &&
          <SelectActivePassModal
            passes={ this.matchingActivePasses }
            seats={ reservation.seats?.length ?? 1 }
            isConfirming={ this.state.isUsingPass }
            onConfirm={ this.useActivePass }
            onClose={ this.toggleSelectPassModal }
          />
        }
        <Modal className="small" isHidden={ !isDeleteModalVisible } onOverlayClick={ this.toggleDeleteModalVisibility }>
          <StyledModalDiv>
            <header>
              <h3>{ LiteralsService.get('deleteReservation', true) }</h3>
              <img src={ images.cross } onClick={ this.toggleDeleteModalVisibility } />
            </header>
            <section className="content">
              {
                reservation.returnDate &&
                <p>
                  { LiteralsService.get('relatedBookingDelete', true) }<br/>
                  <strong>{ reservation.seats[0].exitStop.name } - { reservation.beginningStop.name }, { moment(reservation.returnDate).format('LL HH:mm')  } h</strong>
                </p>
              }
              {
                reservation.ticketingType !== ITicketingType.None &&
                reservation.paymentPrice > 0 &&
                reservation.paymentRefund !== null &&
                <div className="refund-container">
                  <p>
                    <strong>{ LiteralsService.get('refundQuantity', true) }: { reservation.paymentRefund }€</strong><br/>
                  </p>
                  <Link
                    className="link"
                    to={{ pathname: termsLink ?? '' }}
                    target="_blank"
                  >
                    { LiteralsService.get('seeRefundPolitic', true) }
                    <img src={ images.link } alt="externalLink" className='externalLink'/>
                  </Link>
                </div>
              }
              <p>
                { LiteralsService.get(`sureToDelete${reservation.returnDate ? 'Both' : ''}`, true) }
              </p>
              { this.cancellationReasonField }
            </section>
            <footer>
              {
                isDeletingReservation ? 
                <div className='button-container'>
                  <Spinner />
                </div>
                :
                <Button
                  title={ LiteralsService.get('confirm') }
                  onPress={ this.deleteReservation }
                  isDisabled={ this.isCancellationReasonMissing }
                  upper
                  filled
                />
              }
              <Button
                title={ LiteralsService.get('no') }
                onPress={ this.toggleDeleteModalVisibility }
                upper
              />
            </footer>
          </StyledModalDiv>
        </Modal>
        <Modal className="small" isHidden={ !showErrorCancelReservationModal } onOverlayClick={ this.closeErrorCancelReservationModal }>
          <StyledModalDiv>
            <header>
              <h3>{ LiteralsService.get('cancelNotPosibleTitle', true) }</h3>
              <img src={ images.cross } onClick={ this.closeErrorCancelReservationModal } />
            </header>
            <section className="content">
              <p>{ LiteralsService.get('cancelNotPosibleText1', true) }</p>
              <p>
                { LiteralsService.get('cancelNotPosibleText2', true) }
                <Link
                  className="link"
                  to={{ pathname: termsLink ?? '' }}
                  target="_blank"
                >
                  { LiteralsService.get('cancelNotPosibleTerms', true) }
                </Link>
                { LiteralsService.get('cancelNotPosibleText3', true) }
              </p>
            </section>
            <footer>
              <Button
                title={ LiteralsService.get('contact') }
                onPress={ this.contactSupport }
                upper
                filled
              />
              <Button
                title={ LiteralsService.get('close') }
                onPress={ this.closeErrorCancelReservationModal }
                upper
              />
            </footer>
          </StyledModalDiv>
        </Modal>
        <ConfirmPaymentModal
          visible={ isConfirmPaymentModalVisible }
          paymentState={ paymentState }
          isRefund={ this.priceLeftToPay < 0 }
          close={ this.toggleConfirmPaymentModal }
          confirmPayment={ this.confirmPayment }
        />
      </>
    )
  }

  private toggleDeleteModalVisibility = () => {
    // Reopening the modal must not carry over what was typed the previous time.
    this.setState({ isDeleteModalVisible: !this.state.isDeleteModalVisible, cancellationReason: '' })
  }

  // Brands with a configured list get a picker; the rest, free text. Mirrors the app.
  private get cancellationReasonField(): ReactNode {
    const reasons = appsCancelBookingReasonsSelected?.reasons
    const isMandatory = !!appsMandatoryCancellationReasonSelected?.mandatory
    const label = LiteralsService.get(isMandatory ? 'reason' : 'optional', true)
    const { cancellationReason } = this.state

    if (reasons) {
      const options = Object.values(reasons).map(reason => ({
        value: reason as string,
        label: LiteralsService.get((reason as string).toLowerCase(), true)
      }))

      return (
        <div className="cancellation-reason">
          <label>{ label }</label>
          <Select
            value={ options.find(option => option.value === cancellationReason)?.label ?? '' }
            options={ options }
            placeholder={ label }
            onChange={ value => this.setState({ cancellationReason: value }) }
          />
        </div>
      )
    }

    return (
      <div className="cancellation-reason">
        <label htmlFor="cancellation-reason">{ label }</label>
        <textarea
          id="cancellation-reason"
          maxLength={ 200 }
          rows={ 2 }
          value={ cancellationReason }
          onChange={ event => this.setState({ cancellationReason: event.target.value }) }
        />
      </div>
    )
  }

  private get isCancellationReasonMissing(): boolean {
    return !!appsMandatoryCancellationReasonSelected?.mandatory && !this.state.cancellationReason.trim()
  }

  private closeErrorCancelReservationModal = () => {
    this.props.dispatch(setErrorCancelReservationModalAction(false))
  }

  private contactSupport = () => {
    const { clientInfo } = this.props
    if (clientInfo?.phoneNumber) window.open(`tel:${ clientInfo.phoneNumber }`, '_blank')
    else if (clientInfo?.email) window.open(`mailto:${ clientInfo.email }`, '_blank')
  }

  private toggleConfirmPaymentModal = () => {
    this.setState({ isConfirmPaymentModalVisible: !this.state.isConfirmPaymentModalVisible })
  }

  private busIntervalFunction = async (): Promise<void> => {
    const { reservation, dispatch } = this.props

    if (!reservation) return

    let expeditionStatus = IExpeditionStatus.Empty
    try {
      expeditionStatus = await ReservationService.getReservationStatus(reservation.id)
    } catch (error) {
      expeditionStatus = IExpeditionStatus.Closed
    }
    if (reservation.cancelled) expeditionStatus = IExpeditionStatus.Cancelled
    dispatch(setExpeditionStatusAction(expeditionStatus))

    const oldDelay = this.state.delay
    let busLocation = null
    let delay = this.state.delay
    const now = moment()
    const endStopWithDelay = moment(reservation.seats[0].exitStop.dateTime).add(
      delay,
      'minutes'
    )

    if (
      expeditionStatus === IExpeditionStatus.Ready ||
      expeditionStatus === IExpeditionStatus.Ongoing &&
      now.isSameOrBefore(endStopWithDelay)
    ) {
      let response
      try {
        response = await ReservationService.getBusPosition(reservation.id)

        busLocation = {
          latitude: response.latitude,
          longitude: response.longitude,
          color: response.color
        }
        delay = response.delay
      } catch (error) {
        busLocation = null
      }
    }

    this.setState({
      expeditionStatus,
      busLocation,
      delay: delay !== oldDelay ? delay : oldDelay
    })
  }

  private displayFavoriteModal = () => {
    const { reservation, dispatch } = this.props

    if (!reservation?.favorite) {
      dispatch(setSelectedReservationModalAction(reservation))
    }
  }

  private handleQRClick = () => {
    const { reservation, dispatch } = this.props

    dispatch(setQrCodeAndDetails(reservation))
  }

  private openFullscreenMap = () => {
    const { reservation, history } = this.props
    const { busLocation, delay } = this.state

    if (!reservation) return
    history.push(urls.detailsFullscreenMap, { reservation, busLocation, delay })
  }

  private editReservation = () => {
    const { dispatch, history, reservation } = this.props

    if (reservation) {
      dispatch(setNewReservationFieldsAction(reservation))
      history.push(urls.newReservationBusStopsStep)
    }
  }

  private deleteReservation = () => {
    const { reservation, dispatch } = this.props
    const { cancellationReason } = this.state
    // A picked reason travels translated, as the app sends it; free text goes as typed.
    const comment = appsCancelBookingReasonsSelected?.reasons && cancellationReason
      ? LiteralsService.get(cancellationReason.toLowerCase(), true)
      : cancellationReason

    if (reservation) dispatch(deleteReservationAction(reservation.id, comment))
  }

  private confirmPayment = async () => {
    const { reservation, dispatch, location } = this.props
    const { isConfirmPaymentModalVisible } = this.state
    const { id, cancelled } = location.state
    dispatch(setDoingPayment())
    const { notification, success } = await this.confirmStripePayment()
    dispatch(notification)
    let reservationPaymentStatus = success ? reservation.paymentStatus : IPaymentStatus.FAILED
    while (isConfirmPaymentModalVisible && reservationPaymentStatus === IPaymentStatus.CREATED) {
      const { paymentStatus } = await ReservationService.getReservation(reservation.id)
      reservationPaymentStatus = paymentStatus
    }
    dispatch(fetchReservationDetailsAction(id, cancelled))
    this.toggleConfirmPaymentModal()
  }

  private async confirmStripePayment() {
    const { reservation, dispatch } = this.props
    let notification: ISetNotificationAction
    let success = false;
    try {
      this._stripe.elements({
        clientSecret: reservation.paymentClientSecret
      })
      const { paymentIntent } = await this._stripe.confirmPayment({
        clientSecret: reservation.paymentClientSecret,
        redirect: 'if_required'})
      switch (paymentIntent?.status) {
        case 'succeeded':
          success = true
          dispatch(setPaymentSuccess()) 
          notification = setNotificationAction({
            isError: false,
            label: LiteralsService.get('bookPay', true)
          })
          break;
        case 'canceled':
        case 'requires_payment_method':
        default:
          dispatch(setPaymentFailed())
          dispatch(confirmNewReservationFailedAction())
          notification = setNotificationAction({
            isError: true,
            label: LiteralsService.get('payFailed', true)
          })
          break;
      }
    } catch (error) {
      dispatch(setPaymentFailed())
      dispatch(confirmNewReservationFailedAction())
      notification = setNotificationAction({
        isError: true,
        label: LiteralsService.get('payFailed', true)
      })
    } finally {
      return {
        notification: notification!,
        success
      }
    }
  }
}

const mapStateToProps = ({ reservations, configuration, wallet }: IStoreState) => ({
  activePasses: wallet.activePasses,
  reservation: reservations.pickedReservation,
  isDeletingReservation: reservations.isDeletingReservation,
  paymentState: reservations.paymentState,
  termsLink: configuration.clientInfo?.termsUrl,
  showErrorCancelReservationModal: reservations.showErrorCancelReservationModal,
  clientInfo: configuration.clientInfo
})

export default withRouter(connectRedux(mapStateToProps, ReservationDetails as any))
